import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  FormControl,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  FormArray,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { BillChargeDetailComponent } from '../../settings/hospital-setting/bill-charge/bill-charge-detail/bill-charge-detail.component';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { finalize, map, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { LookupService } from 'src/app/services/lookup.service';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BillPrintComponent } from '../bill-print/bill-print.component';
import { NgxPrintModule } from 'ngx-print';
import { BillService } from './bill.service';
import { BillType } from 'src/app/common/enums/bill-type';
import { AppointmentDialog } from '../confirmation-appointment/appointment-dialog.component';
import { PaymentComponent } from '../payment/payment.component';
import { BillPaymentStatus, BillPaymentStatusLabels } from 'src/app/common/enums/bill-payment-status';
import { PaymentListComponent } from '../payment-list/payment-list.component';
import { DiscountType, DiscountTypeLabels } from 'src/app/common/enums/discount-type';
import { InsuranceChargeCategory, InsuranceChargeCategoryLabels } from 'src/app/common/enums/insuranceChargeCategory';
import { Appointment } from '../../appointment/iappointment';
import { IBill } from './ibill';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { IPDBillData } from './ipd-bill-data.interface';
import { BillDialogData } from './bill-dialog-data.interface';
import { MatTableDataSource } from '@angular/material/table';
import { IPaymentList } from '../payment/ipayment';
import { PaymentService } from '../payment/payment.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { PatientService } from '../../patient/patient.service';
import { IPatientInfo } from '../../patient/ipatient-info';
import { DialogUtils } from 'src/app/common/utils/dialog.utils';

@Component({
  selector: 'app-add-bill-charge',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    ReactiveFormsModule,
    FormsModule,
    IndianCurrencyPipe,
    TranslateModule,
    BillPrintComponent,
    NgxPrintModule,
    DefaultValuePipe
  ],
  providers: [
    IndianCurrencyPipe,
  ],
  templateUrl: './bill.component.html',
  styleUrl: './bill.component.scss',
})
export class AddBillChargeComponent implements OnInit {
  // Constants
  private static readonly MINIMUM_STAY_DAYS = 1;
  private static readonly MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
  private static readonly MAX_DISCOUNT_PERCENTAGE = 100;
  private static readonly PRINT_DIALOG_CLOSE_DELAY = 2000;

  // Form properties
  addForm: FormGroup | any;
  rows: UntypedFormArray;
  paymentTypeControl = new FormControl();

  // Data properties
  billCharge: CommonDropDown[] = [];
  filteredBillcharges: Observable<CommonDropDown[]>;
  paymentTypes: CommonDropDown[] = [];
  filteredOptionsForPayment: Observable<CommonDropDown[]>;
  doctorInfo: CommonDropDown;
  hospitalInfo: IHospitalInfo;
  appoitmenttypedetail: CommonDropDown | undefined;

  // Bill properties
  billType: BillType;
  isIPDBill: boolean = false;
  ipdBillData: IPDBillData;
  billDetails: Appointment;
  currentBillId: number | null = null;
  staticRowBillDetailId: number | null = null;
  successResult: IBill | null = null;
  printableBillData: IBill | null = null;
  billNoFromApi: string | null = null;

  // Calculation properties
  subTotal = 0;
  vat = 0;
  grandTotal = 0;
  numberOfDays: number = 1;

  // State properties
  isCash: boolean = false;
  isDiscountFocused = false;
  billSaved: boolean = false;
  printMode = false;
  isEditMode: boolean = false;
  paymentStatus: BillPaymentStatus = BillPaymentStatus.UnPaid;
  isLoading: boolean = false;

  // Tab management
  activeTabIndex: number = 0;

  // Payment summary properties
  paidAmount: number = 0;
  dueAmount: number = 0;
  paymentCount: number = 0;
  payments: IPaymentList[] = [];
  paymentDataSource = new MatTableDataSource<IPaymentList>();
  paymentDisplayedColumns: string[] = ['paymentDate', 'paymentMethod', 'transactionNo', 'description', 'amount'];

  // View references
  @ViewChild('paymentTypeInput') paymentTypeInput!: ElementRef;
  @ViewChildren('chargeInput') chargeInputs!: QueryList<ElementRef>;
  @ViewChild(BillPrintComponent) billPrintComponent!: BillPrintComponent;
  patientInfo: IPatientInfo | null = null;

  // RxJS
  private destroy$ = new Subject<void>();

  // Enums and labels (for template access)
  BillPaymentStatusLabels = BillPaymentStatusLabels;
  DiscountType = DiscountType;
  DiscountTypeLabels = DiscountTypeLabels;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<AddBillChargeComponent>,
    private _lookupService: LookupService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private hospital: HospitalStateService,
    private billService: BillService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private _paymentService: PaymentService,
    private patientService: PatientService,
    @Inject(MAT_DIALOG_DATA) public dialogData: BillDialogData,
    private dialogUtils: DialogUtils,
  ) { }

  ngOnInit(): void {
    this.displayFn = this.displayFn.bind(this);
    this.initializeBillType();
    this.setupForm();
    this.loadBillCharges();
    this.loadPatientDetails(this.getPatientId());
  }

  getInsuranceCategoryName(category?: number | string): string {
    if (!category) return '';

    // If it's a number
    if (typeof category === 'number') {
      return InsuranceChargeCategoryLabels[category as InsuranceChargeCategory] || '';
    }

    // If it's a string enum name (e.g. "RoomBoardingNursingExpenses")
    const enumValue = InsuranceChargeCategory[category as keyof typeof InsuranceChargeCategory];
    if (enumValue !== undefined) {
      return InsuranceChargeCategoryLabels[enumValue as InsuranceChargeCategory] || category;
    }

    return category;
  }

  private initializeBillType(): void {
    this.billType = this.dialogData.billType;
    this.isIPDBill = this.billType === BillType.IPDBill;

    if (this.isIPDBill) {
      this.initializeIPDBill();
    } else {
      this.initializeOPDBill();
    }
  }

  private initializeIPDBill(): void {
    if (!this.dialogData.ipdBillData) return;
    this.ipdBillData = this.dialogData.ipdBillData;
    this.numberOfDays = this.calculateStayDays();

    const totalRoomCharge = this.ipdBillData.roomCharge * this.numberOfDays;
    this.subTotal = totalRoomCharge;
    this.grandTotal = totalRoomCharge;
  }

  private initializeOPDBill(): void {
    const opdData = this.getOPDBillData();
    if (opdData) {
      this.billDetails = opdData;
      const charge = parseFloat(opdData.charge ?? '0') || 0;
      this.subTotal = charge;
      this.grandTotal = charge;
    }
  }

  private loadPatientDetails(patientId: number): void {
    if (!patientId) return;

    this.patientService.getPatientByID(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.isSuccess && res.result) {
          this.patientInfo = res.result;
        }
      });
  }

  private calculateStayDays(): number {
    if (!this.ipdBillData) {
      return AddBillChargeComponent.MINIMUM_STAY_DAYS;
    }

    const admissionDate = new Date(this.ipdBillData.admissionDate);
    const dischargeDate = new Date(this.ipdBillData.dischargeDate);
    const timeDifference = dischargeDate.getTime() - admissionDate.getTime();
    const daysDifference = Math.ceil(timeDifference / AddBillChargeComponent.MILLISECONDS_PER_DAY);

    return Math.max(AddBillChargeComponent.MINIMUM_STAY_DAYS, daysDifference);
  }

  get doctorId(): number | undefined {
    return this.getSelectedDoctorId();
  }

  private getSelectedDoctorId(): number | undefined {
    return this.dialogData.selectedDoctorId;
  }

  private getOPDBillData(): Appointment | null {
    return this.dialogData.opdBillData ?? null;
  }




  private getPatientId(): number {
    if (this.isIPDBill && this.ipdBillData) {
      return this.ipdBillData.patientId;
    }
    const opdData = this.getOPDBillData();
    if (opdData) {
      return Number(opdData.patientId);
    }
    return 0;
  }

  getPatientName(): string {
    if (this.isIPDBill && this.ipdBillData) {
      return this.ipdBillData.patientName;
    }
    const opdData = this.getOPDBillData();
    if (opdData) {
      return opdData.patientName ?? '';
    }
    return '';
  }

  private formatCurrency(value: number): number {
    return parseFloat(value.toFixed(2));
  }

  get opdBillData(): Appointment | undefined {
    return this.dialogData.opdBillData;
  }

  get ipdBillDataForTemplate(): IPDBillData | undefined {
    return this.dialogData.ipdBillData;
  }

  private setupForm(): void {
    this.isCash = false;
    this.hospitalInfo = this.hospital.getHospital();
    this.loadDoctorInfo();
    this.initializeFormControls();
    this.setupDiscountListener();
  }

  private loadDoctorInfo(): void {
    const selectedDoctorId = this.getSelectedDoctorId();

    this._lookupService.doctors$
      .pipe(
        takeUntil(this.destroy$),
        map((doctors: CommonDropDown[]) => {
          if (!selectedDoctorId || !doctors) {
            return undefined;
          }
          return doctors.find(doc => doc.id === Number(selectedDoctorId));
        })
      )
      .subscribe((doctorInfo: CommonDropDown | undefined) => {
        if (doctorInfo) {
          this.doctorInfo = doctorInfo;
        }
      });
  }

  private initializeFormControls(): void {
    this.addForm = this.fb.group({
      billNo: [''],
      patientName: [this.getPatientName()],
      paymentType: this.paymentTypeControl,
      discountType: [DiscountType.FixedAmount],
      discount: [0, Validators.pattern("^[0-9]+(\\.[0-9]{1,2})?$")],
      rows: this.fb.array(Array.from({ length: 1 }, () => this.createItemFormGroup())),
      paymentNo: [''],
      staticChargeRow: this.createStaticChargeRow(),
    });

    this.rows = this.addForm.get('rows') as UntypedFormArray;
  }

  private setupDiscountListener(): void {
    this.addForm.get('discount')?.valueChanges.subscribe(() => {
      this.itemsChanged();
    });
  }

  private createStaticChargeRow(): FormGroup {
    if (this.isIPDBill && this.ipdBillData) {
      const roomCharge = this.ipdBillData.roomCharge;
      const totalCharge = roomCharge * this.numberOfDays;
      const chargeName = this.ipdBillData.roomTypeName;

      return this.fb.group({
        name: [chargeName],
        quantity: [this.numberOfDays],
        rate: [roomCharge],
        total: [{ value: totalCharge, disabled: true }],
        insuranceChargeCategory: []
      });
    }

    // OPD static row
    const opdData = this.getOPDBillData();
    if (opdData) {
      return this.fb.group({
        name: [opdData.appointmentTypeName],
        quantity: [1],
        rate: [opdData.charge],
        total: [{ value: opdData.charge, disabled: true }]
      });
    }

    return this.fb.group({
      name: [''],
      quantity: [1],
      rate: [0],
      total: [{ value: 0, disabled: true }]
    });
  }

  private loadBillCharges(): void {
    this.isLoading = true;
    this._lookupService.billcharges$
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.billCharge = response || [];
        this.filteredBillcharges = of(this.billCharge);
        this.rows.controls.forEach(row => {
          this.attachChargeFilterToRow(row);
        });

        if (this.isIPDBill && this.ipdBillData) {
          const roomChargeData = this.billCharge.find(c => c.id === this.ipdBillData?.roomChargeId);
          if (roomChargeData) {
            this.addForm.get('staticChargeRow')?.get('insuranceChargeCategory')?.setValue(roomChargeData.specialityName);
          }
        }

        let hasPendingDetails = false;
        // Check if editing existing bill
        if (this.isIPDBill && this.ipdBillData) {
          // For IPD, check if there's an existing bill and load payment status
          if (this.ipdBillData.ipdId) {
            hasPendingDetails = true;
            this.loadBillDetailsByIpdId(this.ipdBillData.ipdId, true);
          }
        } else {
          const opdData = this.getOPDBillData();
          if (opdData?.billId) {
            this.isEditMode = true;
            hasPendingDetails = true;
            this.loadBillDetailsByOpdId(Number(opdData.opdId));
          }
        }

        if (!hasPendingDetails) {
          this.isLoading = false;
        }
      });

    // Load appointment type for OPD bills
    if (!this.isIPDBill) {
      const opdData = this.getOPDBillData();
      const appointmentTypeId = opdData?.appointmentTypeId;
      if (appointmentTypeId) {
        this._lookupService.appointmenttype$
          .pipe(takeUntil(this.destroy$))
          .subscribe((response) => {
            if (response) {
              const appointmentType = response.find((item) => item.id === appointmentTypeId);
              this.appoitmenttypedetail = appointmentType;
            }
          });
      }
    }
  }

  displayFnPaymenttype(paymentType: CommonDropDown): string {
    return paymentType && paymentType.name ? paymentType.name : '';
  }

  getSelectedCharges(): number[] {
    return this.rows.controls
      .map((row) => row.get('charge')?.value?.id)
      .filter((id: number) => !!id); // Filter out undefined/null
  }

  getStaticChargeId(): number | null {
    if (this.isIPDBill && this.ipdBillData) {
      return this.ipdBillData.roomChargeId;
    }
    return this.appoitmenttypedetail?.childValue
      ? Number(this.appoitmenttypedetail.childValue)
      : null;
  }

  shouldShowStaticRow(): boolean {
    if (this.hasBillData()) {
      return true;
    }

    return this.hasValidStaticRowData();
  }

  private hasBillData(): boolean {
    if (this.isIPDBill && this.ipdBillData) {
      return true;
    }

    return !!this.getOPDBillData();
  }

  private hasValidStaticRowData(): boolean {
    const staticRowForm = this.addForm.get('staticChargeRow') as FormGroup;
    if (!staticRowForm) {
      return false;
    }

    const rate = Number(staticRowForm.get('rate')?.value || 0);
    const name = staticRowForm.get('name')?.value;

    return rate > 0 && !!name;
  }

  onAddRow(): void {
    const row = this.createItemFormGroup();
    this.attachChargeFilterToRow(row);
    this.rows.push(row);
  }

  private attachChargeFilterToRow(row: AbstractControl): void {
    const control = row.get('charge');
    if (!control) return;

    (row as any).filteredBillcharges$ = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => {
        const filterValue = typeof value === 'string'
          ? value.toLowerCase()
          : value?.name?.toLowerCase() || '';
        const staticId = this.getStaticChargeId();
        const selectedCharges = this.getSelectedCharges();
        const currentChargeId = control?.value?.id;

        return this.billCharge.filter(option =>
          option.name.toLowerCase().includes(filterValue) &&
          (!selectedCharges.includes(option.id) || option.id === currentChargeId) &&
          (staticId === null || option.id !== staticId)
        );
      })
    );
  }

  onRemoveRow(rowIndex: number): void {
    const rowsArray = this.addForm.get('rows') as FormArray;

    if (rowsArray.length <= 1) {
      rowsArray.reset();
    } else {
      rowsArray.removeAt(rowIndex);
    }

    this.itemsChanged();
  }

  createItemFormGroup(): FormGroup {
    const row = this.fb.group({
      id: [null],
      charge: ['', Validators.required],
      quantity: ['', [Validators.required, Validators.pattern("^[0-9]*$")]],
      rate: ['', Validators.required],
      totalAmount: ['0'],
    }, { validators: this.rowValidator });

    this.attachChargeFilterToRow(row);
    return row;
  }

  itemsChanged(): void {
    const staticTotal = this.calculateStaticRowTotal();
    const dynamicTotal = this.calculateDynamicRowsTotal();

    this.subTotal = staticTotal + dynamicTotal;

    const discountAmount = this.calculateDiscountAmount();
    this.grandTotal = Math.max(0, this.subTotal - discountAmount);

    this.cdr.detectChanges();
  }

  private calculateStaticRowTotal(): number {
    const staticRow = this.addForm.get('staticChargeRow') as FormGroup;
    if (!staticRow) {
      return 0;
    }

    const quantity = Number(staticRow.get('quantity')?.value || 0);
    const rate = Number(staticRow.get('rate')?.value || 0);
    const total = quantity * rate;

    staticRow.get('total')?.setValue(total);
    return total;
  }

  private calculateDynamicRowsTotal(): number {
    const rows = this.addForm.get('rows')?.['controls'] || [];

    return rows.reduce((total: number, row: AbstractControl) => {
      const quantity = Number(row.get('quantity')?.value || 0);
      const rate = Number(row.get('rate')?.value || 0);
      return total + quantity * rate;
    }, 0);
  }

  private calculateDiscountAmount(): number {
    const discount = Number(this.addForm.get('discount')?.value || 0);
    const discountType = Number(this.addForm.get('discountType')?.value || DiscountType.FixedAmount);

    const validatedDiscount = this.validateAndAdjustDiscount(discount, discountType);

    if (discountType === DiscountType.Percentage) {
      return this.subTotal * (validatedDiscount / 100);
    }

    return validatedDiscount;
  }

  private validateAndAdjustDiscount(discount: number, discountType: number): number {
    if (discountType === DiscountType.Percentage && discount > AddBillChargeComponent.MAX_DISCOUNT_PERCENTAGE) {
      const adjustedDiscount = AddBillChargeComponent.MAX_DISCOUNT_PERCENTAGE;
      this.addForm.get('discount')?.setValue(adjustedDiscount, { emitEvent: false });
      return adjustedDiscount;
    }

    if (discountType === DiscountType.FixedAmount && this.subTotal > 0 && discount > this.subTotal) {
      const adjustedDiscount = this.subTotal;
      this.addForm.get('discount')?.setValue(adjustedDiscount, { emitEvent: false });
      return adjustedDiscount;
    }

    return discount;
  }

  onDiscountTypeChange(): void {
    this.addForm.get('discount')?.setValue(0);
    this.itemsChanged();
  }


  createBillcharge(row: AbstractControl, typedName?: string): void {
    this.dialog
      .open(BillChargeDetailComponent, {
        width: '600px',
        height: 'auto',
        data: { name: typedName || '' }
      })
      .afterClosed()
      .subscribe((added) => {
        if (added?.result) {
          this.refreshBillChargesList();
          this.updateRowWithNewCharge(row, added.result);
        }
      });
  }

  private refreshBillChargesList(): void {
    this._lookupService.billcharges$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.filteredBillcharges = of(response || []);
      });
  }

  private updateRowWithNewCharge(row: AbstractControl, charge: any): void {
    // Handle both CommonDropDown format and object with charge property
    const chargeAmount = (charge as any).charge
      ? parseFloat((charge as any).charge || '0')
      : parseFloat(charge.childValue || '0');

    row.get('charge')?.setValue(charge);
    row.get('rate')?.setValue(chargeAmount.toFixed(2));
    row.get('quantity')?.setValue(1);
    this.itemsChanged();
  }

  displayFn(billcharge: CommonDropDown): string {
    return billcharge && billcharge.name ? billcharge.name : '';
  }

  onCloseDialog(): void {
    if (!this.billSaved) {
      this.dialogUtils.closeSidePanel(this.dialogRef);
      return;
    }
    const billIdToReturn = this.currentBillId || this.getBillId();
    this.dialogUtils.closeSidePanel(this.dialogRef, billIdToReturn || undefined);
  }

  private buildStaticRow(isUpdate: boolean = false): any {
    const staticRowForm = this.addForm.get('staticChargeRow') as FormGroup;
    const name = staticRowForm.get('name')?.value;
    const insuranceChargeCategory = staticRowForm.get('insuranceChargeCategory')?.value;
    const quantity = Number(staticRowForm.get('quantity')?.value || 0);
    const rate = Number(staticRowForm.get('rate')?.value || 0);
    const totalCharge = quantity * rate;

    const staticRow = this.createStaticRowData(quantity, rate, totalCharge, insuranceChargeCategory);

    if (isUpdate) {
      const billId = this.getBillId();
      if (billId) {
        staticRow.billId = billId;
      }
    }

    return staticRow;
  }

  private createStaticRowData(quantity: number, rate: number, totalCharge: number, insuranceChargeCategory?: string): any {
    if (this.isIPDBill && this.ipdBillData) {
      return {
        chargeId: this.ipdBillData.roomChargeId,
        chargeName: this.ipdBillData.roomTypeName,
        insuranceChargeCategory: insuranceChargeCategory || null,
        quantity: quantity,
        charge: this.formatCurrency(rate),
        totalCharge: this.formatCurrency(totalCharge)
      };
    }

    const opdData = this.getOPDBillData();
    const appointmentChargeId = this.appoitmenttypedetail?.childValue
      ? Number(this.appoitmenttypedetail.childValue)
      : null;

    return {
      chargeId: appointmentChargeId,
      chargeName: opdData?.appointmentTypeName,
      quantity: quantity,
      charge: this.formatCurrency(rate),
      totalCharge: this.formatCurrency(totalCharge)
    };
  }


  private buildDynamicRows(filledRows: any[], isUpdate: boolean): any[] {
    return filledRows.map((row: any) => this.mapFormRowToBillDetail(row, isUpdate));
  }

  private mapFormRowToBillDetail(row: any, isUpdate: boolean): any {
    const quantity = Number(row.quantity);
    const rate = Number(row.rate);
    const totalCharge = quantity * rate;

    const detail: any = {
      chargeId: row.charge.id,
      quantity: quantity,
      charge: this.formatCurrency(rate),
      totalCharge: this.formatCurrency(totalCharge),
      chargeName: row.charge.name
    };

    if (isUpdate && row.id) {
      detail.id = row.id;
      const billId = this.getBillId();
      if (billId) {
        detail.billId = billId;
      }
    }

    return detail;
  }

  private buildPayload(staticRow: any, dynamicRows: any[], isUpdate: boolean): any {
    const formValue = this.addForm.value;
    const billId = this.getBillId();
    const billDetails = this.buildBillDetailsArray(staticRow, dynamicRows, isUpdate, billId);

    const basePayload: any = {
      ...(isUpdate && billId && { id: billId }),
      billType: this.billType,
      patientId: this.getPatientId(),
      paymentTypeId: formValue.paymentType?.id,
      discountType: formValue.discountType,
      discount: parseFloat(formValue.discount || 0),
      amount: this.grandTotal,
      subTotal: this.subTotal,
      paymentNo: formValue.paymentNo,
      billDetails: billDetails
    };

    this.addBillTypeSpecificFields(basePayload);
    return basePayload;
  }

  private buildBillDetailsArray(
    staticRow: any,
    dynamicRows: any[],
    isUpdate: boolean,
    billId: number | null
  ): any[] {
    if (!this.hasValidStaticRowData()) {
      return dynamicRows;
    }

    if (isUpdate && billId) {
      const staticRowWithId = this.staticRowBillDetailId
        ? { ...staticRow, id: this.staticRowBillDetailId, billId: billId }
        : { ...staticRow, billId: billId };
      return [staticRowWithId, ...dynamicRows];
    }

    return [staticRow, ...dynamicRows];
  }

  private addBillTypeSpecificFields(payload: any): void {
    if (this.isIPDBill && this.ipdBillData) {
      payload.ipdId = this.ipdBillData.ipdId;
      payload.opdId = null;
    } else {
      const opdData = this.getOPDBillData();
      payload.opdId = opdData?.opdId ?? null;
      payload.ipdId = null;
    }
  }

  private getIsUpdate(): boolean {
    if (this.isIPDBill) {
      return !!this.currentBillId;
    }
    const opdData = this.getOPDBillData();
    return !!opdData?.billId || !!this.currentBillId;
  }

  private getBillId(): number | null {
    if (this.isIPDBill) {
      return this.currentBillId;
    }
    return this.dialogData.opdBillData?.billId ?? this.currentBillId;
  }

  saveUpdateBill(): void {
    const formValue = this.addForm.value;
    const validationResult = this.validateBillRows(formValue.rows);

    if (!validationResult.isValid) {
      this.toastr.error(this.translate.instant('VALIDATION.QUANTITY_RATE'));
      return;
    }

    const isUpdate = this.getIsUpdate();
    const staticRow = this.buildStaticRow(isUpdate);
    const dynamicRows = this.buildDynamicRows(validationResult.filledRows, isUpdate);
    const payload = this.buildPayload(staticRow, dynamicRows, isUpdate);

    this.isLoading = true;
    const request$ = isUpdate
      ? this.billService.updateBill(payload)
      : this.billService.createBill(payload);

    request$.pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((response) => {
      if (response.isSuccess) {
        this.handleBillSaveSuccess(response, isUpdate);
      } else {
        this.handleBillSaveError(response);
      }
    });
  }

  private validateBillRows(rows: any[]): { isValid: boolean; filledRows: any[] } {
    let hasPartialRow = false;
    const filledRows = rows.filter((row: any, index: number) => {
      const chargeValid = row.charge && typeof row.charge === 'object' && row.charge.id;
      const qtyValid = row.quantity !== '' && row.quantity !== null && !isNaN(row.quantity) && Number(row.quantity) > 0;
      const rateValid = row.rate !== '' && row.rate !== null && !isNaN(row.rate) && Number(row.rate) > 0;

      if (chargeValid && (!qtyValid || !rateValid)) {
        this.rows.at(index).markAllAsTouched();
        hasPartialRow = true;
      }

      return chargeValid && qtyValid && rateValid;
    });

    return { isValid: !hasPartialRow, filledRows };
  }

  private handleBillSaveSuccess(response: any, isUpdate: boolean): void {
    this.successResult = response.result;

    if (response.result) {
      this.paymentStatus = this.convertPaymentStatusToEnum(response.result.paymentStatus);
      this.currentBillId = response.result.id ?? null;
      this.billNoFromApi = response.result.billNo ?? null;
      this.isEditMode = !!response.result.id;

      // Update static row ID from response if it's a new bill
      if (!isUpdate && response.result.billDetails) {
        const staticChargeId = this.getStaticChargeId();
        const staticDetail = response.result.billDetails.find(
          (detail: any) => detail.chargeId === staticChargeId
        );
        if (staticDetail) {
          this.staticRowBillDetailId = staticDetail.id || null;
        }
      }
    }

    this.toastr.success(
      isUpdate
        ? this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.BILL'))
        : this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.BILL'))
    );
    this.billSaved = true;

    // Load payment history after successful save
    if (this.currentBillId) {
      this.loadPaymentHistory();
    }
  }

  private fetchBillForPrint(billId: number, closeDialogOnError: boolean = false): void {
    const fetchObservable = this.getBillFetchObservable();

    if (!fetchObservable) {
      if (closeDialogOnError) {
        this.handleBillFetchError(billId, 'No bill ID available');
      } else {
        this.toastr.error(this.translate.instant('ERROR.FAILED_TO_LOAD_BILL') || 'Failed to load bill data for printing');
      }
      return;
    }

    fetchObservable.pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (res) => this.handleBillFetchResponse(res, billId, false),
      error: () => {
        if (closeDialogOnError) {
          this.handleBillFetchError(billId);
        } else {
          this.toastr.error(this.translate.instant('ERROR.FAILED_TO_LOAD_BILL') || 'Failed to load bill data for printing');
        }
      }
    });
  }

  private getBillFetchObservable(): Observable<any> | null {
    if (this.isIPDBill && this.ipdBillData?.ipdId) {
      return this.billService.getBillDetailByIpdId(this.ipdBillData.ipdId);
    }

    const opdData = this.getOPDBillData();
    if (opdData?.opdId) {
      return this.billService.getBillDetailByOpdId(Number(opdData.opdId));
    }

    return null;
  }

  private handleBillFetchResponse(res: any, billId: number, showPrintDialog: boolean = false): void {
    if (!res.isSuccess || !res.result) {
      this.handleBillFetchError(billId, 'Invalid response from server');
      return;
    }

    const bill = Array.isArray(res.result) ? res.result[0] : res.result;

    if (!bill) {
      this.handleBillFetchError(billId, 'No bill data found');
      return;
    }

    this.updateFormWithBillData(bill);
    this.printableBillData = this.buildPrintPayloadFromBill(bill);

    if (showPrintDialog) {
      this.showPrintConfirmationDialog(billId);
    } else {
      this.triggerPrint(billId);
    }
  }

  private handleBillFetchError(billId: number, customMessage?: string): void {
    const errorMessage = customMessage ||
      this.translate.instant('ERROR.FAILED_TO_LOAD_BILL') ||
      'Failed to load bill data for printing';

    this.toastr.error(errorMessage);
    this.dialogRef.close(billId);
  }


  private updateFormWithBillData(bill: IBill): void {
    // Update form values
    this.patchFormValues(bill);

    // Separate static charge from dynamic charges
    const staticChargeId = this.getStaticChargeId();
    // Use name as fallback for identification (available synchronously from dialogData)
    const expectedStaticName = this.isIPDBill
      ? this.ipdBillData?.roomTypeName
      : this.opdBillData?.appointmentTypeName;

    // Helper function (single source of truth)
    const isStatic = (detail: IBill['billDetails'][0]) =>
      (staticChargeId && detail.chargeId === staticChargeId) ||
      (expectedStaticName && detail.chargeName === expectedStaticName);

    // Safe optional chaining
    const staticChargeDetail = bill.billDetails?.find(isStatic);

    // Safe filter with fallback
    const dynamicCharges = bill.billDetails?.filter(detail => !isStatic(detail));

    // Update static charge row if found
    if (staticChargeDetail) {
      this.staticRowBillDetailId = staticChargeDetail.id || null;
      const staticRowForm = this.addForm.get('staticChargeRow') as FormGroup;
      staticRowForm.patchValue({
        name: staticChargeDetail.chargeName,
        quantity: staticChargeDetail.quantity,
        rate: staticChargeDetail.charge,
        total: staticChargeDetail.totalCharge,
        insuranceChargeCategory: this.getInsuranceCategoryName((staticChargeDetail as any).insuranceChargeCategoryName)
      });
    }

    // Update dynamic rows
    this.patchBillDetails(dynamicCharges);

    // Recalculate totals using itemsChanged() which includes static row
    // Don't override with bill totals as they might not reflect current form state
    this.itemsChanged();
  }

  private buildPrintPayloadFromBill(bill: IBill): any {
    const formValue = this.addForm.value;
    const staticChargeId = this.getStaticChargeId();

    // Map all bill details to print format
    const allDetails = (bill.billDetails || []).map(detail => {
      const charge = this.billCharge.find(c => c.id === detail.chargeId);
      return {
        chargeId: detail.chargeId,
        chargeName: detail.chargeName,
        quantity: detail.quantity,
        rate: detail.charge,
        total: detail.totalCharge,
        insuranceChargeCategory: this.getInsuranceCategoryName((detail as any).insuranceChargeCategoryName) || (charge ? this.getInsuranceCategoryName(charge.insuranceChargeCategoryId) : '')
      }
    });

    // If static charge not in DB details, add from form
    if (staticChargeId && !allDetails.some(d => d.chargeId === staticChargeId)) {
      const staticRow = this.getStaticRowFromForm(staticChargeId);
      if (staticRow) allDetails.unshift(staticRow);
    }

    const result: any = {
      billType: bill.billType || this.billType,
      billNo: bill.billNo || this.billNoFromApi,
      patientName: bill.patientName || this.getPatientName(),
      paymentTypeName: formValue.paymentType?.name,
      discountType: bill.discountType ?? formValue.discountType,
      discount: bill.discount ?? parseFloat(formValue.discount || 0),
      grandTotal: bill.amount ?? this.grandTotal,
      subTotal: bill.subTotal ?? this.subTotal,
      paymentStatus: bill.paymentStatus ?? this.paymentStatus,
      billDetails: allDetails,
      amountInWords: bill.amountInWords,
      billDate: bill.billDate,
      relativeName: bill.relativeName,
      bedNo: bill.bedNo,
      gender: bill.gender || this.patientInfo?.gender,
      dob: bill.dob || this.patientInfo?.birthDate,
      age: bill.age || this.patientInfo?.age,
      mobileNumber: this.patientInfo?.contactInfo?.[0]?.contact,
      address: this.formatPatientAddress(this.patientInfo),
    };


    this.addBillTypeFields(result);
    return result;
  }

  private handleBillSaveError(response: any): void {
    const allErrors = Object.values(response.errors).flat();
    this.toastr.error(allErrors.join("\n"));
  }

  private showPrintConfirmationDialog(billId: number): void {
    const confirmation = this.dialog.open(AppointmentDialog, {
      width: '350px',
      data: {
        message: this.translate.instant('LABEL.PRINT_BILL_CONFIRMATION'),
        title: this.translate.instant('ENTITY.PRINT_BILL_CONFIRMATION')
      }
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === "yes") {
        this.triggerPrint(billId);
      } else {
        this.dialogRef.close(billId);
      }
    });
  }

  private triggerPrint(billId: number): void {
    this.setupPrintCallbacks(billId);

    if (this.billPrintComponent) {
      this.billPrintComponent.createPrintContent().then(content => {
        this.openPrintWindow(content, billId);
      }).catch(err => {
        this.dialogRef.close(billId);
      });
    } else {
      this.dialogRef.close(billId);
    }
  }

  private openPrintWindow(content: string, billId: number): void {
    const printFrame = document.createElement('iframe');
    printFrame.id = 'print-iframe';
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);

    const doc = printFrame.contentWindow?.document || printFrame.contentDocument;
    if (doc) {
      doc.open();
      doc.write(content);
      doc.close();

      const cleanup = () => {
        this.dialogRef.close(billId);
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      };

      // Give images and styles time to load
      setTimeout(() => {
        if (printFrame.contentWindow) {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.onafterprint = cleanup;
          printFrame.contentWindow.print();
        } else {
          cleanup(); // Cleanup if printing fails to start
        }
      }, 500);
    } else {
      this.dialogRef.close(billId);
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    }
  }

  private setupPrintCallbacks(billId: number): void {
    window.onafterprint = () => {
      this.dialogRef.close(billId);
      window.onafterprint = null;
    };
  }

  printBill(): void {
    const billId = this.getBillId();

    if (billId && (this.billSaved || this.isEditMode)) {
      this.fetchBillForPrint(billId);
      return;
    }

    if (this.successResult) {
      this.printableBillData = this.buildPrintPayloadFromBill(this.successResult);
      this.printMode = true;
      return;
    }

    this.printFromFormData();
  }


  private printFromFormData(): void {
    this.printableBillData = this.buildPrintPayloadFromForm();

    if ((this.printableBillData?.billDetails?.length ?? 0) > 0) {
      this.printMode = true;
    } else {
      this.toastr.error(
        this.translate.instant('ERROR.NO_BILL_DATA_TO_PRINT') || 'No bill data available to print'
      );
    }
  }


  focusOnLastChargeInput(index: number): void {
    if (index < 0) {
      return;
    }

    setTimeout(() => {
      const input = this.chargeInputs?.last?.nativeElement;
      if (input) {
        input.focus({ preventScroll: true });
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  rowValidator(group: FormGroup): ValidationErrors | null {
    const charge = group.get('charge')?.value;
    const quantity = group.get('quantity')?.value;
    const rate = group.get('rate')?.value;

    const isAnyFilled = charge || quantity || rate;

    if (!isAnyFilled) {
      return null;
    }

    if (!charge || !quantity || !rate) {
      return { incompleteRow: true };
    }
    return null;
  }

  hasAtLeastOneValidRow(): boolean {
    return this.rows.controls.some((row: AbstractControl) => {
      const charge = row.get('charge')?.value;
      const quantity = row.get('quantity')?.value;
      const rate = row.get('rate')?.value;
      return charge && quantity && rate;
    });
  }

  onPaymentTypeChange(selectedValue: CommonDropDown): void {
    this.isCash = selectedValue.childValue === 'true';
    const paymentNoControl = this.addForm.get('paymentNo');

    if (this.isCash) {
      paymentNoControl?.reset();
      paymentNoControl?.clearValidators();
    } else {
      paymentNoControl?.setValidators([Validators.required]);
    }

    paymentNoControl?.updateValueAndValidity();
  }


  onChargeSelected(selectedCharge: CommonDropDown | string, row: FormGroup): void {
    if (typeof selectedCharge === 'string') {
      this.createBillcharge(row, selectedCharge);
      return;
    }

    if (selectedCharge?.childValue) {
      const chargeAmount = parseFloat(selectedCharge.childValue || '0');
      row.get('rate')?.setValue(chargeAmount.toFixed(2));
      row.get('quantity')?.setValue(1);

      // Ensure the charge object in the form control has insuranceChargeCategory
      const currentCharge = row.get('charge')?.value;
      if (currentCharge && selectedCharge.specialityName) {
        currentCharge.insuranceChargeCategory = selectedCharge.specialityName;
        row.get('charge')?.setValue(currentCharge);
      }

      this.itemsChanged();
    }
  }

  shouldShowAddOption(row: FormGroup): boolean {
    const value = row.get('charge')?.value;
    const options = (row as any).filteredBillchargesSnapshot || [];

    if (!value || typeof value !== 'string') return false;

    const match = options.some((opt: { name: string; }) => opt.name.toLowerCase() === value.toLowerCase());

    return !match;
  }

  isChargeSelected(row: AbstractControl): boolean {
    const value = row.get('charge')?.value;
    return !!(value && (typeof value === 'object' ? value.name : value));
  }

  isChargeAlreadySelected(charge: CommonDropDown, currentRow: FormGroup): boolean {
    const selectedIds = this.getSelectedCharges();
    const currentId = currentRow.get('charge')?.value?.id;
    const staticId = this.getStaticChargeId();
    return charge.id !== currentId && (selectedIds.includes(charge.id) || (staticId !== null && charge.id === staticId));
  }

  loadBillDetailsByOpdId(opdId: number, shouldPatchForm: boolean = true): void {
    this.isLoading = true;
    this.billService.getBillDetailByOpdId(opdId).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((res) => {
      if (res.isSuccess && res.result && res.result.length > 0) {
        this.handleBillLoadResponse(res.result[0], shouldPatchForm);
      }
    });
  }

  patchBillForm(data: IBill | IBill[]): void {
    const billData = this.normalizeBillData(data);
    // Use updateFormWithBillData to properly separate static charge and update form
    this.updateFormWithBillData(billData);
    this.printableBillData = this.buildPrintPayloadFromBill(billData);
  }

  private normalizeBillData(data: IBill | IBill[]): IBill {
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : {} as IBill;
    }
    return data;
  }

  private patchFormValues(billData: IBill): void {
    const selectedPaymentType = this.paymentTypes.find(pt => pt.id === billData.paymentTypeId) || null;
    this.isCash = selectedPaymentType?.childValue === 'true';

    this.addForm.patchValue({
      billNo: billData.billNo,
      patientName: billData.patientName,
      paymentType: selectedPaymentType,
      discountType: DiscountType[billData.discountType],
      discount: billData.discount,
      paymentNo: billData.paymentNo
    });

    if (billData.subTotal !== undefined && billData.subTotal !== null) {
      this.subTotal = billData.subTotal;
    }
  }

  private patchBillDetails(billDetails: IBill['billDetails']): void {
    this.rows.clear();

    if (!billDetails || billDetails.length === 0) {
      // If no dynamic rows exist, check if extra row should be added
      this.addExtraRowIfNeeded();
      return;
    }

    const seenIds = new Set<number>();
    billDetails.forEach(item => {
      // Avoid duplicate dynamic rows
      if (!item.chargeId || !seenIds.has(item.chargeId)) {
        if (item.chargeId) seenIds.add(item.chargeId);
        const row = this.createBillDetailRow(item);
        this.rows.push(row);
      }
    });
  }

  private createBillDetailRow(item: IBill['billDetails'][0]): FormGroup {
    const row = this.createItemFormGroup();
    const charge = this.billCharge.find(c => c.id === item.chargeId)
      || (item.chargeId ? { id: item.chargeId, name: item.chargeName ?? '' } as CommonDropDown : null);

    this.mapBillDetailToFormRow(row, item, charge);
    this.attachChargeFilterToRow(row);

    return row;
  }

  private mapBillDetailToFormRow(
    row: FormGroup,
    item: IBill['billDetails'][0],
    charge: CommonDropDown | null
  ): void {
    row.patchValue({
      id: item.id,
      charge: charge,
      quantity: item.quantity,
      rate: item.charge,
      totalAmount: item.totalCharge
    });
  }


  private buildPrintPayloadFromForm(): any {
    const formValue = this.addForm.value;
    const staticChargeId = this.getStaticChargeId();
    const billDetails: any[] = [];

    // Add static row from form if it has valid data
    if (staticChargeId) {
      const staticRow = this.getStaticRowFromForm(staticChargeId);
      if (staticRow) billDetails.push(staticRow);
    }

    // Add dynamic rows from form
    const filledRows = (formValue.rows || []).filter((row: any) =>
      row.charge?.id && row.quantity && row.rate
    );
    filledRows.forEach((row: any) => {
      billDetails.push({
        chargeId: row.charge.id,
        chargeName: row.charge.name,
        quantity: Number(row.quantity),
        rate: this.formatCurrency(Number(row.rate)),
        total: this.formatCurrency(row.quantity * row.rate),
        insuranceChargeCategory: row.charge.insuranceChargeCategory
      });
    });

    const result: any = {
      billType: this.billType,
      billNo: formValue.billNo || this.billNoFromApi,
      patientName: this.getPatientName(),
      paymentTypeName: formValue.paymentType?.name,
      discountType: formValue.discountType,
      discount: parseFloat(formValue.discount || 0),
      grandTotal: this.grandTotal,
      subTotal: this.subTotal,
      paymentStatus: this.paymentStatus,
      billDetails,
      gender: this.patientInfo?.gender,
      dob: this.patientInfo?.birthDate,
      age: this.patientInfo?.age,
      mobileNumber: this.patientInfo?.contactInfo?.[0]?.contact,
      address: this.formatPatientAddress(this.patientInfo),
    };

    this.addBillTypeFields(result);
    return result;
  }

  private formatPatientAddress(patient: IPatientInfo | null): string {
    if (!patient || !patient.address || patient.address.length === 0) return '';
    
    // Find permanent and current addresses using isPermanent field
    const permanentAddr = patient.address.find(addr => addr.isPermanent === true);
    const currentAddr = patient.address.find(addr => addr.isPermanent === false);
    
    // Priority: permanent address first, fallback to current
    const addr = permanentAddr || currentAddr;
    
    if (!addr) return '';
    
    const parts = [
        addr.address1, 
        addr.address2, 
        addr.area, 
        addr.taluka, 
        addr.district, 
        addr.state, 
        addr.pincode
    ].filter(part => !!part);
    
    return parts.join(', ');
}

  private getStaticRowFromForm(staticChargeId: number): any | null {
    const staticRowForm = this.addForm.get('staticChargeRow') as FormGroup;
    if (!staticRowForm) return null;

    const name = staticRowForm.get('name')?.value;
    const quantity = Number(staticRowForm.get('quantity')?.value || 0);
    const rate = Number(staticRowForm.get('rate')?.value || 0);
    const insuranceChargeCategory = staticRowForm.get('insuranceChargeCategory')?.value;

    if (rate <= 0 || !name) return null;

    return {
      chargeId: staticChargeId,
      chargeName: name,
      quantity,
      rate: this.formatCurrency(rate),
      total: this.formatCurrency(quantity * rate),
      insuranceChargeCategory: insuranceChargeCategory || null
    };
  }

  private addBillTypeFields(result: any): void {
    if (this.isIPDBill && this.ipdBillData) {
      result.ipdNumber = this.ipdBillData.ipdNumber;
      result.admissionDate = this.ipdBillData.admissionDate;
      result.dischargeDate = this.ipdBillData.dischargeDate;
      result.numberOfDays = this.numberOfDays;
    } else {
      const opdData = this.getOPDBillData();
      result.opdNumber = opdData?.opdNumber;
      result.opdDate = opdData?.appointDate;
    }
  }

  get canPrint(): boolean {
    const billId = this.getBillId();
    return !!billId || !!this.successResult?.id;
  }

  private convertPaymentStatusToEnum(status: string | number | BillPaymentStatus | undefined): BillPaymentStatus {
    if (status === undefined || status === null) {
      return BillPaymentStatus.UnPaid;
    }

    // If already a number (enum value), return as-is
    if (typeof status === 'number') {
      return status as BillPaymentStatus;
    }

    // Convert string to enum
    const statusString = String(status);
    switch (statusString) {
      case 'Paid':
      case '1':
        return BillPaymentStatus.Paid;
      case 'PartialPaid':
      case '2':
        return BillPaymentStatus.PartialPaid;
      case 'UnPaid':
      case 'Unpaid':
      case '3':
        return BillPaymentStatus.UnPaid;
      default:
        return BillPaymentStatus.UnPaid;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  shouldShowPaymentsTab(): boolean {
    return !!this.currentBillId;
  }

  loadPaymentHistory(): void {
    if (!this.currentBillId) return;
    this._paymentService.getPatientPaymentHistory(this.currentBillId).pipe(
    ).subscribe({
      next: (response) => {
        if (response.isSuccess && response.result) {
          const payments = Array.isArray(response.result) ? response.result : [response.result];
          // Map transactionNumber to transactionNo for template compatibility
          this.payments = payments.map(payment => ({
            ...payment,
            transactionNo: payment.transactionNumber,
            description: payment.description
          }));
          this.paymentDataSource.data = this.payments;
          this.paymentCount = this.payments.length;
          this.calculatePaymentSummary();
        } else {
          this.paymentCount = 0;
        }
        // Check if extra row should be added after payment count is updated
        this.addExtraRowIfNeeded();
      },
      error: () => {
        this.payments = [];
        this.paymentCount = 0;
        this.calculatePaymentSummary();
        // Check if extra row should be added after payment count is updated
        this.addExtraRowIfNeeded();
      }
    });
  }

  calculatePaymentSummary(): void {
    this.paidAmount = this.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    this.dueAmount = Math.max(0, this.grandTotal - this.paidAmount);
  }

  private addExtraRowIfNeeded(): void {
    if (this.isEditMode && this.paymentCount === 0 && this.rows.length === 0) {
      this.onAddRow();
    }
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    if (index === 1 && this.shouldShowPaymentsTab()) {
      this.loadPaymentHistory();
    }
  }

  takePayment(): void {
    if (!this.currentBillId || this.dueAmount <= 0) return;

    this.dialog.open(PaymentComponent, {
      autoFocus: false,
      height: '60vh',
      maxWidth: '80vw',
      data: { billId: this.currentBillId }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.reloadBillAfterPayment();
        this.loadPaymentHistory();
      }
    });
  }

  isInvoiceEditingDisabled(): boolean {
    return this.paymentStatus === BillPaymentStatus.Paid;
  }

  makePayment(): void {
    const billId = this.getBillId();
    if (!billId) {
      return;
    }

    this.dialog.open(PaymentComponent, {
      autoFocus: false,
      height: '60vh',
      maxWidth: '80vw',
      data: { billId: billId }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.reloadBillAfterPayment();
      }
    });
  }

  private reloadBillAfterPayment(): void {
    this.billSaved = true;

    if (this.isIPDBill && this.ipdBillData?.ipdId) {
      this.loadBillDetailsByIpdId(this.ipdBillData.ipdId);
      return;
    }

    const opdData = this.getOPDBillData();
    if (opdData?.opdId) {
      this.loadBillDetailsByOpdId(opdData.opdId);
    }
  }

  shouldShowPaymentButton(): boolean {
    return this.paymentStatus === BillPaymentStatus.PartialPaid ||
      this.paymentStatus === BillPaymentStatus.UnPaid;
  }

  patientPaymentList(): void {
    const billId = this.getBillId();
    this.dialog.open(PaymentListComponent, {
      autoFocus: false,
      width: '70vh',
      height: 'auto',
      maxWidth: '195vh',
      data: {
        billno: this.billNoFromApi,
        patientName: this.getPatientName(),
        billId: billId
      }
    }).afterClosed().subscribe(() => {
      // Payment list dialog closed - no action needed
    });
  }

  loadBillDetailsByIpdId(ipdId: number, shouldPatchForm: boolean = true): void {
    this.isLoading = true;
    this.billService.getBillDetailByIpdId(ipdId).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((res) => {
      if (res.isSuccess && res.result && res.result.length > 0) {
        this.handleBillLoadResponse(res.result[0], shouldPatchForm);
      }
    });
  }

  private handleBillLoadResponse(bill: IBill, shouldPatchForm: boolean): void {
    this.currentBillId = bill.id || null;
    this.isEditMode = true;
    this.billNoFromApi = bill.billNo || null;

    if (bill.paymentStatus !== undefined) {
      this.paymentStatus = this.convertPaymentStatusToEnum(bill.paymentStatus);
    }

    if (shouldPatchForm) {
      this.patchBillForm(bill);
    }

    if (this.currentBillId) {
      this.loadPaymentHistory();
    }
  }

}
